import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    Drawer,
    IconButton,
    Paper,
    Stack,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import { knowledgeApi, KnowledgeEntryDetail } from '../../../api/knowledgeApi';

interface Props {
    entryId: string | null;
    onClose: () => void;
    onReindex: (id: string) => Promise<void>;
    onDelete: (id: string) => Promise<void>;
}

/**
 * Right-side drawer showing entry detail + a sample of its chunks. Used
 * by admins to QA "is the bot actually getting the content I expect?".
 */
export const KnowledgeDetailDrawer: React.FC<Props> = ({ entryId, onClose, onReindex, onDelete }) => {
    const [entry, setEntry] = useState<KnowledgeEntryDetail | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!entryId) {
            setEntry(null);
            setError(null);
            return;
        }
        let cancelled = false;
        setLoading(true);
        setError(null);
        knowledgeApi
            .get(entryId)
            .then((e) => { if (!cancelled) setEntry(e); })
            .catch((err: any) => {
                if (!cancelled) {
                    setError(err?.response?.data?.message || err?.message || 'Failed to load entry');
                }
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [entryId]);

    const open = !!entryId;

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{ sx: { width: { xs: '100%', sm: 560 }, p: 2 } }}
        >
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Typography variant="h6">Knowledge entry</Typography>
                <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
            </Stack>

            {loading && (
                <Box sx={{ textAlign: 'center', mt: 4 }}>
                    <CircularProgress size={28} />
                </Box>
            )}

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {entry && (
                <Stack spacing={2}>
                    <Box>
                        <Typography variant="overline" color="text.secondary">Title</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>{entry.title}</Typography>
                    </Box>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        <Chip size="small" label={`source: ${entry.sourceType}`} />
                        <Chip size="small" label={`status: ${entry.indexStatus}`} color={entry.indexStatus === 'READY' ? 'success' : entry.indexStatus === 'FAILED' ? 'error' : 'default'} />
                        <Chip size="small" label={`${entry.chunkCount} chunks`} />
                        <Chip size="small" label={`audience: ${entry.scope?.audience ?? 'WORKER'}`} variant="outlined" />
                        {(entry.scope?.profession ?? []).map((p) => <Chip key={p} size="small" color="primary" variant="outlined" label={p} />)}
                        {(entry.scope?.language ?? []).map((l) => <Chip key={l} size="small" color="secondary" variant="outlined" label={l} />)}
                    </Stack>

                    {entry.indexStatus === 'FAILED' && entry.indexErrorMessage && (
                        <Alert severity="error">
                            <Typography variant="caption" component="div" sx={{ fontFamily: 'monospace' }}>
                                {entry.indexErrorMessage}
                            </Typography>
                        </Alert>
                    )}

                    <Stack direction="row" spacing={1} alignItems="center">
                        <Button
                            size="small"
                            startIcon={<RefreshIcon />}
                            onClick={() => onReindex(entry.id)}
                        >
                            {entry.sourceType === 'URL' ? 'Re-fetch & reindex' : 'Reindex'}
                        </Button>
                        <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => onDelete(entry.id)}>Delete</Button>
                        {entry.sourceType === 'URL' && (
                            <Typography variant="caption" color="text.secondary">
                                URL entries re-fetch the source page; FILE / paste re-chunk the stored snapshot.
                            </Typography>
                        )}
                    </Stack>

                    <Divider />

                    <Box>
                        <Typography variant="overline" color="text.secondary">Sample chunks</Typography>
                        <Stack spacing={1.5} sx={{ mt: 1 }}>
                            {entry.sampleChunks.length === 0 && (
                                <Typography variant="body2" color="text.secondary">No chunks yet.</Typography>
                            )}
                            {entry.sampleChunks.map((c) => (
                                <Paper key={c.id} variant="outlined" sx={{ p: 1.5 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        Chunk {c.chunkIndex} {c.charStart != null ? `(chars ${c.charStart}–${c.charEnd})` : ''}
                                    </Typography>
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
                                        {c.text}
                                    </Typography>
                                </Paper>
                            ))}
                        </Stack>
                    </Box>

                    {entry.rawContent && (
                        <Box>
                            <Typography variant="overline" color="text.secondary">Extracted text (first 2k chars)</Typography>
                            <Paper variant="outlined" sx={{ p: 1.5, maxHeight: 200, overflow: 'auto' }}>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                                    {entry.rawContent.slice(0, 2000)}
                                    {entry.rawContent.length > 2000 ? '…' : ''}
                                </Typography>
                            </Paper>
                        </Box>
                    )}
                </Stack>
            )}
        </Drawer>
    );
};
